import {
  useIsMutating,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { type AxiosError } from 'axios';
import { useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { validate as uuidValidate } from 'uuid';

import { toast } from '@hivebrite/design-system/Toast';

import {
  type Cart,
  type CartItem,
  type CartItems,
  type CartItemsAttributes,
  type ShoppingCardResponse,
} from 'hb-react/shared/components/ShoppingCard/hooks/v2';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';
import { type Currency } from 'hb-react/types';

const createCartRequest = ({
  cart_items: cartItems,
  currency,
}: {
  cart_items: Array<CartItem>;
  currency?: string;
}) =>
  axios.post<ShoppingCardResponse>(
    Routes.order_management_carts_v2_index_path(),
    {
      cart: {
        currency: currency ?? cartItems[0].currency,
        cart_items: cartItems,
      },
    },
  );

export const useShoppingCard = (id?: string) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { cartId = id } = useParams<{ cartId: string }>();

  const queryClient = useQueryClient();

  const onSuccess = async ({ data }) => {
    const key = ['cart', data.cart.id];

    await queryClient.cancelQueries({
      queryKey: key,
    });

    queryClient.setQueryData(key, () => data);
  };

  const onError = (error: AxiosError<{ errors?: Array<string> }>) => {
    errorReporting.captureException(error);
    toast.error(
      error.response?.data?.errors?.join(',') || __('Sorry, an error occurred'),
    );
  };

  const baseQueryOptions = {
    onError,
    onSuccess,
  };

  const { mutateAsync: init } = useMutation({
    mutationFn: (params: { cart: Pick<Cart, 'currency'> }) =>
      axios.post<ShoppingCardResponse>(
        Routes.init_order_management_carts_v2_index_path(),
        params,
      ),
    ...baseQueryOptions,
  });

  const { mutateAsync: reset } = useMutation({
    mutationFn: ({
      user,
      ...cart
    }: {
      billing_address_attributes?: Cart['billing_address'];
      cart_items: Array<CartItemsAttributes>;
      currency: Currency;
      metadata: {
        [key: string]: unknown;
      };
      shipping_address_attributes?: Cart['shipping_address'];
      user?: {
        email?: string;
        firstname?: string;
        lastname?: string;
      };
    }) =>
      axios.post<ShoppingCardResponse>(
        Routes.reset_order_management_carts_v2_path(cartId!),
        {
          cart,
          user,
        },
      ),
    ...baseQueryOptions,
  });

  const { mutateAsync: create } = useMutation({
    mutationFn: createCartRequest,
    ...baseQueryOptions,
  });

  const { mutateAsync: updateCart } = useMutation({
    mutationFn: (cart) =>
      axios.put(Routes.order_management_carts_v2_path(cartId!), {
        cart,
      }),
    ...baseQueryOptions,
  });

  const { mutate: add } = useMutation({
    mutationFn: (...items: Array<CartItem>) =>
      axios.post(Routes.order_management_carts_v2_cart_items_path(cartId!), {
        cart: {
          cart_items: items,
        },
      }),
    ...baseQueryOptions,
  });

  const { mutate: remove } = useMutation({
    mutationFn: (id: number) =>
      axios.delete(
        Routes.order_management_carts_v2_cart_item_path(cartId!, id),
      ),
    ...baseQueryOptions,
  });

  const { mutate: update } = useMutation({
    mutationFn: (item: CartItem) =>
      axios.patch(
        Routes.order_management_carts_v2_cart_item_path(cartId!, item.id),
        {
          cart: { cart_items: [item] },
        },
      ),

    ...baseQueryOptions,
  });

  const { mutateAsync: complete } = useMutation({
    mutationFn: (payload: { captcha_response?: string } | void = {}) =>
      axios.post(Routes.order_management_orders_path(), {
        cart_uuid: cartId!,
        ...payload,
      }),
    onError,
  });

  const { mutateAsync: addPromoCode } = useMutation({
    mutationFn: (code: string) =>
      axios.post(
        Routes.validate_promo_code_order_management_carts_v2_path(cartId!),
        { promo_code: { code } },
      ),
    onSuccess: ({ data }) => {
      onSuccess({ data });
    },
  });

  const { data, isLoading } = useQuery({
    queryKey: ['cart', cartId],
    queryFn: () =>
      axios
        .get<ShoppingCardResponse>(
          Routes.order_management_carts_v2_path(cartId!),
        )
        .then(({ data }) => data),

    enabled: uuidValidate(cartId!),
    refetchOnWindowFocus: false,
    staleTime: 300000,
  });

  const isMutating = useIsMutating();

  const actions = useMemo(
    () => ({
      init,
      create,
      reset,
      update: updateCart,
      complete,
      items: {
        update,
        remove,
        add,
        addPromoCode,
      },
    }),
    [
      add,
      addPromoCode,
      complete,
      create,
      init,
      remove,
      reset,
      update,
      updateCart,
    ],
  );

  const cartItems = useMemo(() => {
    const items: Array<CartItems> = data?.cart?.cart_items ?? [];

    return items.map<CartItems>((item) => {
      const { line_itemable_type: type } = item;
      let onEdit;
      let onDelete;

      switch (type) {
        case 'Donation::Campaign':
          onEdit = () => navigate(`${location.pathname}/donations`);
          break;
        case 'Ecommerce::PromoCode':
          onDelete = () => remove(item.id);
          break;
        default:
          break;
      }

      return {
        ...item,
        onEdit,
        onDelete,
      };
    });
  }, [data?.cart?.cart_items, location.pathname, navigate, remove]);

  const cart = useMemo(
    () =>
      data?.cart && {
        ...data.cart,
        cart_items: cartItems,
      },
    [data?.cart, cartItems],
  );

  const dataWithTransformedCartItems = useMemo(
    () => ({
      ...data,
      cart,
    }),
    [data, cart],
  );

  return {
    cartId,
    data: dataWithTransformedCartItems,
    cart,
    metadata: data?._metadata,
    isLoading: Boolean(isLoading),
    isMutating: Boolean(isMutating),
    actions,
  };
};
